import { useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  type AuthProvider,
  type AuthCredential,
  type User,
  type UserCredential,
} from "firebase/auth";
import { auth } from "../firebase";

// Popups are unreliable/blocked on mobile browsers (iOS Safari throws
// auth/argument-error) — those get the redirect flow instead.
const POPUP_UNSUPPORTED = new Set([
  "auth/argument-error",
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
  "auth/web-storage-unsupported",
]);
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// When a second provider (e.g. Apple) is used on an email that already has an
// account (e.g. Google), Firebase blocks it with account-exists-with-different-
// credential. We stash the new credential, have the user sign in with their
// ORIGINAL method, then link the two so both buttons reach one account.
const PENDING_KEY = "fusePendingLinkV1";

function stashPending(e: unknown): string | null {
  const err = e as { customData?: { email?: string } };
  const cred =
    OAuthProvider.credentialFromError(e as never) ??
    GoogleAuthProvider.credentialFromError(e as never);
  const email = err?.customData?.email;
  if (!cred || !email) return null;
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ cred: cred.toJSON(), email }));
  return email;
}

/** After a successful sign-in, link any stashed credential for the SAME email. */
async function linkPending(user: User): Promise<void> {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return;
  sessionStorage.removeItem(PENDING_KEY);
  try {
    const { cred, email } = JSON.parse(raw) as { cred: object; email: string };
    // Only link if it's genuinely the same email (different email = different person).
    if (!user.email || user.email.toLowerCase() !== email.toLowerCase()) return;
    const credential = OAuthProvider.credentialFromJSON(cred) as AuthCredential;
    await linkWithCredential(user, credential);
  } catch {
    /* already linked or credential expired — the user is signed in either way */
  }
}

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Complete any redirect round-trip (mobile OAuth), and finish a pending link.
  useEffect(() => {
    getRedirectResult(auth)
      .then((res) => { if (res?.user) return linkPending(res.user); })
      .catch(async (e: unknown) => {
        const code = (e as { code?: string })?.code ?? "";
        if (code === "auth/account-exists-with-different-credential") {
          await onAccountExists(e);
        } else {
          setError(pretty(e));
        }
      });
  }, []);

  async function afterSignIn(res: UserCredential) {
    await linkPending(res.user);
  }

  /** Existing account with a different provider — guide the user to link. */
  async function onAccountExists(e: unknown) {
    const email2 = stashPending(e);
    if (!email2) { setError(pretty(e)); return; }
    setEmail(email2);
    // Try to name their original method (may be hidden by email-enumeration
    // protection, in which case we give a generic nudge).
    let hint = "the method you used before";
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email2);
      if (methods.includes("google.com")) hint = "Continue with Google";
      else if (methods.includes("apple.com")) hint = "Continue with Apple";
      else if (methods.includes("password")) hint = "your email and password";
    } catch { /* enumeration protection — keep the generic hint */ }
    setError(`You already have a Fuse account for ${email2}. Sign in with ${hint} — we'll connect this new sign-in to it automatically.`);
  }

  async function emailContinue() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      await afterSignIn(res);
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code ?? "";
      if (code === "auth/user-not-found" || code === "auth/invalid-credential") {
        try {
          const res = await createUserWithEmailAndPassword(auth, email, password);
          await afterSignIn(res);
        } catch (e2: unknown) {
          setError(pretty(e2));
        }
      } else {
        setError(pretty(e));
      }
    } finally {
      setBusy(false);
    }
  }

  function google() {
    return oauth(new GoogleAuthProvider());
  }

  async function oauth(provider: AuthProvider) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (isMobile) {
        await signInWithRedirect(auth, provider); // navigates away; linked in getRedirectResult
        return;
      }
      const res = await signInWithPopup(auth, provider);
      await afterSignIn(res);
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code ?? "";
      if (code === "auth/account-exists-with-different-credential") {
        await onAccountExists(e);
      } else if (POPUP_UNSUPPORTED.has(code)) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (e2: unknown) {
          setError(pretty(e2));
        }
      } else if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setError(pretty(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!email) return;
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setNotice("Password reset email sent — check your inbox.");
    } catch (e: unknown) {
      setError(pretty(e));
    }
  }

  return (
    <div className="screen">
      <div style={{ height: 32 }} />
      <div className="brand">Fuse</div>
      <div className="tagline">Every match gets 10 messages.<br />Make them count.</div>

      <label>Email</label>
      <input
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
      />
      <label>Password</label>
      <input
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
      />

      <button className="btn" disabled={busy || !email || password.length < 6} onClick={emailContinue}>
        {busy ? "…" : "Continue with Email"}
      </button>
      <p className="muted center" style={{ fontSize: 13, margin: "10px 0 0" }}>
        New here? Enter any email and a password (6+ characters) — your account is
        created automatically.
      </p>
      <button className="btn btn-ghost" onClick={reset} disabled={!email}>
        Forgot password?
      </button>

      <div className="divider">or</div>

      <button className="btn btn-secondary" onClick={() => void google()} disabled={busy}>
        Continue with Google
      </button>

      {notice && <p className="muted center" style={{ marginTop: 10 }}>{notice}</p>}
      {error && <div className="error">{error}</div>}

      <p className="muted center" style={{ fontSize: 12, marginTop: 22 }}>
        By continuing, you agree to Fuse's{" "}
        <a href="/terms">Terms</a>,{" "}
        <a href="/guidelines">Community Guidelines</a>, and{" "}
        <a href="/privacy">Privacy Policy</a>. Fuse is for adults 18+.
      </p>
    </div>
  );
}

function pretty(e: unknown): string {
  const code = (e as { code?: string })?.code ?? "";
  const map: Record<string, string> = {
    "auth/invalid-email": "That email doesn't look right.",
    "auth/weak-password": "Password should be at least 6 characters.",
    "auth/email-already-in-use":
      "That email already has a Fuse account, but this password doesn't match. Use its original password, or tap 'Forgot password?' to reset it.",
    "auth/wrong-password": "Incorrect password.",
    "auth/too-many-requests": "Too many attempts — try again shortly.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/argument-error": "That sign-in method hiccuped — try again, or use email.",
    "auth/operation-not-allowed": "That sign-in method isn't enabled yet — use Google or email for now.",
    "auth/account-exists-with-different-credential":
      "That email already has a Fuse account. Sign in with the method you used before to connect them.",
  };
  return map[code] ?? (e as { message?: string })?.message ?? "Something went wrong.";
}
